import * as React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { height } from "@mui/system";
import { CircularProgress } from "@mui/material";
import { colorBrandObject } from "../../constants/brandings";
import { readableColor, rgba } from "polished";

export default function CustomButton({
  text,
  variant,
  fontSize,
  fontWeight,
  lineHeigth,
  padding,
  color,
  borderRadius,
  disabled,
  onClick,
  type,
  secondaryType,
  size,
  width,
  height,
  id,
  iconButton,
  loading,
  icon,
  sx,
  containerStyle
}) {
  const colorMap = {
    primary: {
      main: colorBrandObject?.primaryButtonColor,
      contrastText: readableColor(colorBrandObject?.primaryButtonColor),
      hoverMain: rgba(colorBrandObject?.primaryButtonColor, 0.85),
      hoverContrastText: readableColor(colorBrandObject?.primaryButtonColor),
      outlinedMain: "#fff",
      outlinedContrastText: colorBrandObject?.primaryButtonColor,
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: colorBrandObject?.primaryButtonColor,
    },
    primary2: {
      main: colorBrandObject?.primaryButtonColor2,
      contrastText: "#403E9F",
      hoverMain: "#C7C6FF",
      hoverContrastText: "#403E9F",
      outlinedMain: "#fff",
      outlinedContrastText: "#403E9F",
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: "#403E9F",
      outlinedBorder: "#E6E6E6", // Added border color for outlined type
      outlinedHoverBorder: "#403E9F", // Border color on hover
    },
    primaryLight: {
      main: "#E4E3FF",
      contrastText: "#4F4D55",
      hoverMain: "rgba(199, 198, 255, 1)",
      hoverContrastText: "rgba(10, 9, 11, 1)",
      outlinedMain: "#fff",
      outlinedContrastText: "#403E9F",
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: "#403E9F",
    },
    secondary: {
      main: colorBrandObject?.secondaryButtonColor,
      hoverMain: rgba(colorBrandObject?.secondaryButtonColor, 0.85),
      // hoverContrastText: readableColor(colorBrandObject?.secondaryButtonColor),
      hoverContrastText: "#fff",
      outlinedMain: "#fff",
      outlinedMain: "#fff",
      outlinedContrastText: colorBrandObject?.secondaryButtonColor,
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: colorBrandObject?.secondaryButtonColor,
    },
    secondaryLight: {
      main: "#D5F3F0",
      contrastText: "#4F4D55",
      hoverMain: "rgba(172, 222, 218, 1)",
      hoverContrastText: "rgba(10, 9, 11, 1)",
      outlinedMain: "#fff",
      outlinedContrastText: "#24B7AA",
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: "#24B7AA",
    },
    error: {
      main: "#FF4D4F",
      contrastText: "#fff",
      hoverMain: "rgba(150, 22, 22, 1)",
      hoverContrastText: "#fff",
      outlinedMain: "#fff",
      outlinedContrastText: "#FF4D4F",
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: "#FF4D4F",
    },
    info: {
      main: "#24B7AA",
      contrastText: "#fff",
      hoverMain: "#2E2C8D",
      hoverContrastText: "#fff",
      outlinedMain: "#fff",
      outlinedContrastText: "#403E9F",
      outlinedHoverMain: "#fff",
      outlinedHoverContrastText: "#403E9F",
    },
  };

  const sizeMap = {
    xxxl: {
      height: "60px",
      padding: "0px 24px",
      fontSize: "24px",
      fontWeight: "500",
      lineHeight: iconButton ? "20px" : "32px",
    },
    xxl: {
      height: "40px",
      padding: "0px 24px",
      fontSize: "16px",
      fontWeight: "500",
      lineHeight: iconButton ? "18px" : "20px",
    },
    xl: {
      height: "48px",
      fontSize: "18px",
      fontWeight: "400",
      lineHeigth: "24px",
      padding: iconButton ? "12px" : "0px 16px",
    },
    lg: {
      height: "44px",
      fontSize: "16px",
      fontWeight: "400",
      lineHeigth: "22px",
      padding: iconButton ? "10px" : "0px 14px",
    },
    md: {
      height: "40px",
      fontSize: "14px",
      fontWeight: "400",
      lineHeigth: "20px",
      padding: iconButton ? "8px" : "0px 14px",
    },
    sm: {
      height: "36px",
      fontSize: "14px",
      fontWeight: "400",
      lineHeigth: "20px",
      padding: iconButton ? "6px" : "0px 12px",
    },
    xs: {
      height: "32px",
      fontSize: "14px",
      fontWeight: "400",
      lineHeigth: "20px",
      padding: iconButton ? "0px" : "0px 10px",
    },
  };

  const theme = createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            width: [width],
            height: sizeMap[size]["height"],
            fontSize: sizeMap[size]["fontSize"],
            fontWeight: sizeMap[size]["fontWeight"],
            lineHeigth: sizeMap[size]["lineHeigth"],
            padding: sizeMap[size]["padding"],
            fontFamily: "Poppins",
            textTransform: "none",
            borderRadius: [borderRadius],
            minWidth: "auto",

            "&.MuiButton-outlinedPrimary": {
              backgroundColor: colorMap[type]["outlinedMain"],
              color: colorMap[type]["outlinedContrastText"],
              border: "1px solid rgba(230, 230, 230, 1)",
              "&:hover": {
                backgroundColor: colorMap[type]["outlinedHoverMain"],
                color: colorMap[type]["outlinedHoverContrastText"],
                border: `1px solid ${colorMap[type]["outlinedHoverContrastText"]}`,
              },
              "&:disabled": {
                backgroundColor: "rgba(241, 241, 241, 1)",
                color: "rgba(201, 201, 204, 1)",
              },
            },
            "&.MuiButton-containedPrimary": {
              backgroundColor: colorMap[type]["main"],
              color: colorMap[type]["contrastText"],
              "&:hover": {
                backgroundColor: [colorMap[type]["hoverMain"]],
                color: [colorMap[type]["hoverContrastText"]],
              },
              "&:disabled": {
                backgroundColor: "rgba(241, 241, 241, 1)",
                color: "rgba(201, 201, 204, 1)",
              },
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            fontSize: sizeMap[size]["fontSize"],
            color: colorMap[type]["contrastText"],
          },
        },
      },
    },
  });

  function debounce(func, delay = 2000) {
    let timer;
    return function () {
      const context = this;
      const args = arguments;
      if (!timer) {
        func.apply(context, args);
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
      }, delay);
    };
  }

  const dbFunc = debounce((event, onClick) => {
    onClick(event);
  });

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          position: "relative",
          ...containerStyle
        }}
      >
        <Button
          id={id}
          disabled={disabled}
          variant={variant}
          disableElevation
          disableRipple
          color={color}
          onClick={(event) => dbFunc(event, onClick)}
          loadingPosition="start"
          sx={sx}
        >
          {/* Reserve space for the text even when loading */}
          <span style={{ visibility: loading ? "hidden" : "visible" }}>
            {text}
          </span>
        </Button>

        {loading && (
          <div
            style={{
              position: "absolute",
              top: "54%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              zIndex: "999"
            }}
          >

            <CircularProgress size={16} sx={{ color: colorBrandObject[color || "primary"] }} />
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

CustomButton.defaultProps = {
  text: "Submit",
  variant: "outlined",
  color: "primary",
  fontSize: "14px",
  fontWeight: "400",
  lineHeigth: "18px",
  padding: "0px 24px 0px 24px",
  borderRadius: "8px",
  disabled: false,
  width: "auto",
  height: "none",
  type: "primary",
  size: "sm",
  secondaryType: "secondary",
  id: "",
  iconButton: false,
  onClick: () => { },
};
